import React from "react";
import { Grid, Typography } from "@mui/material";
import DetailRow from "../../pages/common-features/DetailRow";
import { useSelector } from "react-redux";
import { RootState } from "../../services/redux/store";
import SummarySectionHeader from "./SummarySectionHeader";
import { formatDate, parseAndFormatDate } from "../../utils/dateFormatUtils";

interface EntranceExamDetailsTabProps {
  entranceDetails: any;
  isMobile: boolean;
  pageType?: string;
}

const EntranceExamDetailsTab: React.FC<EntranceExamDetailsTabProps> = ({
  entranceDetails,
  isMobile,
  pageType = "DEFAULT",
}) => {
  const { fetchUserApplicationData } = useSelector(
    (state: RootState) => state.stepper
  );

  const formatExpectedOnDate = (value: any): string => {
    const raw = String(value ?? "").trim();
    if (!raw) return "";
    if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) return raw;
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
      const formatted = formatDate(raw);
      return formatted !== "-" ? formatted : raw;
    }
    const parsed = parseAndFormatDate(raw);
    return parsed || raw;
  };

  const isScoreReceivedYes = (value: any): boolean => {
    const raw = String(value ?? "").trim().toLowerCase();
    return value === true || raw === "yes" || raw === "true";
  };

  const isScoreReceivedNo = (value: any): boolean => {
    const raw = String(value ?? "").trim().toLowerCase();
    return value === false || raw === "no" || raw === "false";
  };

  // Helper to map entrance exam details from the vertical step to the required format
  function mapEntranceExamDetailsFromVerticalStep(verticalStep: any) {
    if (!verticalStep || !verticalStep?.fields) return null;
    // Find and map core and language exam fields
    let coreExamApplied = null;
    let coreExamDetails: any[] = [];
    let languageExamApplied = null;
    let languageExamDetails: any[] = [];

    // Group core exam fields by suffix
    const coreGroups: Record<string, any> = {};
    const languageGroups: Record<string, any> = {};

    verticalStep?.fields.forEach((fieldObj: any) => {
      const key = Object.keys(fieldObj)?.[0];
      const value = fieldObj?.[key];
      // Suffix extraction: e.g. coreExamName_6 => _6
      const coreMatch = key.match(
        /^(coreExamName|coreScoreReceived|coreExamScore|coreTotalMarks|coreScoreExpectedOn)(_(\d+))?$/
      );
      const langMatch = key.match(
        /^(languageExamName|languageScoreReceived|languageExamScore|languageTotalMarks|languageScoreExpectedOn)(_(\d+))?$/
      );
      if (key === "coreExamApplied") {
        coreExamApplied = {
          key,
          label: "Have you applied for core exam?",
          value,
        };
      } else if (coreMatch) {
        const suffix = coreMatch[2] || "";
        if (!coreGroups[suffix]) coreGroups[suffix] = {};
        // Format label
        let label = "";
        if (coreMatch[1] === "coreExamName") label = "Core Exam Name";
        else if (coreMatch[1] === "coreScoreReceived")
          label = "Core Score Received";
        else if (coreMatch[1] === "coreExamScore") label = "Core Exam Score";
        else if (coreMatch[1] === "coreTotalMarks")
          label = "Core Exam Total Marks";
        else if (coreMatch[1] === "coreScoreExpectedOn")
          label = "Score Expected On";
        coreGroups[suffix][coreMatch[1]] = { key, label, value };
      } else if (key === "languageExamApplied") {
        languageExamApplied = {
          key,
          label: "Have you appeared for language exam?",
          value,
        };
      } else if (langMatch) {
        const suffix = langMatch[2] || "";
        if (!languageGroups[suffix]) languageGroups[suffix] = {};
        let label = "";
        if (langMatch[1] === "languageExamName") label = "Language Exam Name";
        else if (langMatch[1] === "languageScoreReceived")
          label = "Language Score Received";
        else if (langMatch[1] === "languageExamScore")
          label = "Language Exam Score";
        else if (langMatch[1] === "languageTotalMarks")
          label = "Language Exam Total Marks";
        else if (langMatch[1] === "languageScoreExpectedOn")
          label = "Score Expected On";
        languageGroups[suffix][langMatch[1]] = { key, label, value };
      }
    });

    // Flatten grouped core exam details into rows
    coreExamDetails = Object.keys(coreGroups).map((suffix) => {
      console.log("Processing core group with suffix:", suffix, coreGroups[suffix]);
      const group = coreGroups[suffix];
      return {
        examName: group.coreExamName?.value || "",
        scoreReceived: group.coreScoreReceived?.value || "",
        examScore: group.coreExamScore?.value || "",
        totalMarks: group.coreTotalMarks?.value || "",
        scoreExpectedOn: group.coreScoreExpectedOn?.value || "",
        labels: {
          examName: group.coreExamName?.label || "Core Exam Name",
          scoreReceived:
            group.coreScoreReceived?.label || "Core Score Received",
          examScore: group.coreExamScore?.label || "Core Exam Score",
          totalMarks: group.coreTotalMarks?.label || "Core Exam Total Marks",
          scoreExpectedOn:
            group.coreScoreExpectedOn?.label || "Score Expected On",
        },
      };
    });
    // Flatten grouped language exam details into rows
    languageExamDetails = Object.keys(languageGroups).map((suffix) => {
      const group = languageGroups[suffix];
      return {
        examName: group.languageExamName?.value || "",
        scoreReceived: group.languageScoreReceived?.value || "",
        examScore: group.languageExamScore?.value || "",
        totalMarks: group.languageTotalMarks?.value || "",
        scoreExpectedOn: group.languageScoreExpectedOn?.value || "",
        labels: {
          examName: group.languageExamName?.label || "Language Exam Name",
          scoreReceived:
            group.languageScoreReceived?.label || "Language Score Received",
          examScore: group.languageExamScore?.label || "Language Exam Score",
          totalMarks:
            group.languageTotalMarks?.label || "Language Exam Total Marks",
          scoreExpectedOn:
            group.languageScoreExpectedOn?.label || "Score Expected On",
        },
      };
    });

    return {
      coreExamApplied,
      coreExamDetails,
      languageExamApplied,
      languageExamDetails,
    };
  }

  const defaultEntranceDetails = {
    coreExamApplied: { label: "Have you applied for core exam?", value: "" },
    coreExamDetails: [],
    languageExamApplied: {
      label: "Have you appeared for language exam?",
      value: "",
    },
    languageExamDetails: [],
  };

  const mergeExamRows = (primaryRows: any[] = [], fallbackRows: any[] = []) => {
    const maxLength = Math.max(primaryRows.length, fallbackRows.length);
    const merged: any[] = [];
    for (let i = 0; i < maxLength; i += 1) {
      const primary = primaryRows[i] || {};
      const fallback = fallbackRows[i] || {};
      merged.push({
        examName: primary.examName ?? fallback.examName ?? "",
        scoreReceived: primary.scoreReceived ?? fallback.scoreReceived ?? "",
        examScore: primary.examScore ?? fallback.examScore ?? "",
        totalMarks: primary.totalMarks ?? fallback.totalMarks ?? "",
        scoreExpectedOn: primary.scoreExpectedOn ?? fallback.scoreExpectedOn ?? "",
        labels: {
          examName: primary?.labels?.examName || fallback?.labels?.examName || "Exam Name",
          scoreReceived:
            primary?.labels?.scoreReceived ||
            fallback?.labels?.scoreReceived ||
            "Score Received",
          examScore:
            primary?.labels?.examScore || fallback?.labels?.examScore || "Exam Score",
          totalMarks:
            primary?.labels?.totalMarks || fallback?.labels?.totalMarks || "Total Marks",
          scoreExpectedOn:
            primary?.labels?.scoreExpectedOn || fallback?.labels?.scoreExpectedOn || "Score Expected On",
        },
      });
    }
    return merged;
  };

  const mergeEntranceDetails = (primary: any, fallback: any) => ({
    coreExamApplied: {
      label:
        primary?.coreExamApplied?.label ||
        fallback?.coreExamApplied?.label ||
        defaultEntranceDetails.coreExamApplied.label,
      value:
        primary?.coreExamApplied?.value ??
        fallback?.coreExamApplied?.value ??
        defaultEntranceDetails.coreExamApplied.value,
    },
    coreExamDetails: mergeExamRows(
      primary?.coreExamDetails || [],
      fallback?.coreExamDetails || []
    ),
    languageExamApplied: {
      label:
        primary?.languageExamApplied?.label ||
        fallback?.languageExamApplied?.label ||
        defaultEntranceDetails.languageExamApplied.label,
      value:
        primary?.languageExamApplied?.value ??
        fallback?.languageExamApplied?.value ??
        defaultEntranceDetails.languageExamApplied.value,
    },
    languageExamDetails: mergeExamRows(
      primary?.languageExamDetails || [],
      fallback?.languageExamDetails || []
    ),
  });

  // Normalize API shape when partnerStartedJourney is true
  // Incoming shape example:
  // coreExamDetails: [{ coreExam1: [{ label, key, value }, ...] }, { coreExam2: [...]}]
  // languageExamDetails: [{ languageExam1: [...] }, ...]
  function normalizeEntranceDetailsFromApiShape(raw: any) {
    if (!raw || typeof raw !== "object") return null;

    const normalizeGroupArray = (arr: any[]) => {
      if (!Array.isArray(arr)) return [];
      return arr.map((obj: any) => {
        const fieldsArr = Array.isArray(obj)
          ? obj
          : (Object.values(obj)?.[0] as any[]);
        if (!Array.isArray(fieldsArr))
          return {
            examName: "",
            scoreReceived: "",
            examScore: "",
            totalMarks: "",
            scoreExpectedOn: "",
            labels: {
              examName: "Exam Name",
              scoreReceived: "Score Received",
              examScore: "Exam Score",
              totalMarks: "Total Marks",
              scoreExpectedOn: "Score Expected On",
            },
          };

        const findByLabel = (contains: string) =>
          fieldsArr.find(
            (f: any) =>
              typeof f?.label === "string" &&
              f.label.toLowerCase().includes(contains)
          );

        const nameField = findByLabel("exam name");
        const scoreReceivedField = findByLabel("score received");
        const examScoreField = findByLabel("exam score");
        const totalMarksField = findByLabel("total marks");
        const scoreExpectedOnField = findByLabel("score expected on");

        return {
          examName: nameField?.value ?? "",
          scoreReceived: scoreReceivedField?.value ?? "",
          examScore: examScoreField?.value ?? "",
          totalMarks: totalMarksField?.value ?? "",
          scoreExpectedOn: scoreExpectedOnField?.value ?? "",
          labels: {
            examName: nameField?.label || "Exam Name",
            scoreReceived: scoreReceivedField?.label || "Score Received",
            examScore: examScoreField?.label || "Exam Score",
            totalMarks: totalMarksField?.label || "Total Marks",
            scoreExpectedOn:
              scoreExpectedOnField?.label || "Score Expected On",
          },
        };
      });
    };

    return {
      coreExamApplied: raw.coreExamApplied,
      coreExamDetails: normalizeGroupArray(raw.coreExamDetails),
      languageExamApplied: raw.languageExamApplied,
      languageExamDetails: normalizeGroupArray(raw.languageExamDetails),
    };
  }

  let entranceDetailsToShow = entranceDetails;
  // If partner journey data returns new API shape, normalize it
  const looksLikeApiShape =
    entranceDetailsToShow &&
    ((Array.isArray(entranceDetailsToShow?.coreExamDetails) &&
      entranceDetailsToShow.coreExamDetails.some((obj: any) =>
        Array.isArray((Object.values(obj) || [])[0]),
      )) ||
      (Array.isArray(entranceDetailsToShow?.languageExamDetails) &&
        entranceDetailsToShow.languageExamDetails.some((obj: any) =>
          Array.isArray((Object.values(obj) || [])[0]),
        )));
  if (
    fetchUserApplicationData &&
    fetchUserApplicationData?.partnerStartedJourney &&
    looksLikeApiShape
  ) {
    entranceDetailsToShow = normalizeEntranceDetailsFromApiShape(
      entranceDetailsToShow
    );
  }

  if (
    pageType === "SUMMARY" &&
    fetchUserApplicationData &&
    !fetchUserApplicationData?.partnerStartedJourney
  ) {
    // Find the 'Applicant & Loan Details' horizontal step
    const applicantLoanStep = fetchUserApplicationData?.horizontalSteps?.find(
      (h: any) => h.horizontalStepName === "Applicant & Loan Details"
    );
    // Find the 'Entrance Exam Details' vertical step
    const entranceExamDetailsStep = applicantLoanStep?.verticalSteps?.find(
      (v: any) => v.verticalStepName === "Entrance Exam Details"
    );
    const fallbackEntranceDetails = entranceDetails || defaultEntranceDetails;
    if (entranceExamDetailsStep) {
      console.log('coming Entrance Exam Details to show')
      const mappedEntranceDetails = mapEntranceExamDetailsFromVerticalStep(
        entranceExamDetailsStep
      );
      entranceDetailsToShow = mergeEntranceDetails(
        mappedEntranceDetails,
        fallbackEntranceDetails
      );
      console.log(mappedEntranceDetails, 'fallbackEntranceDetails')
    } else {
      entranceDetailsToShow = mergeEntranceDetails({}, fallbackEntranceDetails);
    }
  }
  const enteranceNoDataFlag =
    (!entranceDetailsToShow?.coreExamDetails?.length &&
      entranceDetailsToShow?.coreExamApplied?.value === "") &&
    (!entranceDetailsToShow?.languageExamDetails?.length &&
      entranceDetailsToShow?.languageExamApplied?.value === "");
  if (enteranceNoDataFlag) {
    return (
      <Grid item xs={12}>
        <Typography variant="body2" color="textSecondary">
          No details available.
        </Typography>
      </Grid>
    );
  }

  return (
    <>
      {/* Core Exam Section */}
      <Grid item xs={12} sx={{ my: 1, mx: isMobile ? 1 : 0 }}>
        <SummarySectionHeader title="Core Exam" />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <DetailRow
          item={{
            label:
              entranceDetailsToShow.coreExamApplied?.label ||
              'Have you applied for core exam?',
            value: (() => {
              const raw = entranceDetailsToShow.coreExamApplied?.value;
              const str = String(raw).toLowerCase();

              if (str === 'yes' || str === 'true') return 'Yes';
              if (str === 'no' || str === 'false') return 'No';

              return 'No'; // default fallback
            })(),
            isVerified: false,
          }}
        />
      </Grid>
      {/* Render grouped core exam rows */}
      {entranceDetailsToShow.coreExamApplied?.value &&
        Array.isArray(entranceDetailsToShow.coreExamDetails) &&
        entranceDetailsToShow.coreExamDetails.map((row: any, idx: number) => (
          <Grid item xs={12} key={`core-row-${idx}`} sx={{ mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3} md={3}>
                <DetailRow
                  item={{
                    label: row?.labels?.examName?.replace?.(/^Core /, '') ?? '',
                    value: row?.examName ?? '',
                    isVerified: false,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3} md={3}>
                <DetailRow
                  item={{
                    label:
                      row?.labels?.scoreReceived?.replace?.(/^Core /, '') ?? '',
                    value:
                      typeof row?.scoreReceived === 'boolean'
                        ? row?.scoreReceived
                          ? "Yes"
                          : "No"
                        : (row?.scoreReceived ?? ""),
                    isVerified: false,
                  }}
                />
              </Grid>
              {isScoreReceivedYes(row?.scoreReceived) && (
                  <>
                    <Grid item xs={12} sm={3} md={3}>
                      <DetailRow
                        item={{
                          label:
                            row?.labels?.examScore?.replace?.(/^Core /, '') ??
                            '',
                          value: row?.examScore ?? '',
                          isVerified: false,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3} md={3}>
                      <DetailRow
                        item={{
                          label:
                            row?.labels?.totalMarks?.replace?.(/^Core /, '') ??
                            '',
                          value: row?.totalMarks ?? '',
                          isVerified: false,
                        }}
                      />
                    </Grid>
                  </>
                )}
              {isScoreReceivedNo(row?.scoreReceived) && (
                  <Grid item xs={12} sm={3} md={3}>
                    <DetailRow
                      item={{
                        label:
                          row?.labels?.scoreExpectedOn?.replace?.(
                            /^Core /,
                            ''
                          ) ?? '',
                        value: formatExpectedOnDate(row?.scoreExpectedOn),
                        isVerified: false,
                      }}
                    />
                  </Grid>
                )}
            </Grid>
          </Grid>
        ))}

      {/* Language Exam Section */}
      <Grid item xs={12} sx={{ my: 1, mx: isMobile ? 1 : 0 }}>
        <SummarySectionHeader title="Language Exam" />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <DetailRow
          item={{
            label:
              entranceDetailsToShow.languageExamApplied?.label ||
              'Have you appeared for language exam?',
            value: (() => {
              const raw = entranceDetailsToShow.languageExamApplied?.value;
              const str = String(raw).toLowerCase();

              if (str === 'yes' || str === 'true') return 'Yes';
              if (str === 'no' || str === 'false') return 'No';

              return 'No'; // default fallback
            })(),
            isVerified: false,
          }}
        />
      </Grid>
      {/* Render grouped language exam rows */}
      {entranceDetailsToShow.languageExamApplied?.value &&
        Array.isArray(entranceDetailsToShow.languageExamDetails) &&
        entranceDetailsToShow.languageExamDetails.map(
          (row: any, idx: number) => (
            <Grid item xs={12} key={`language-row-${idx}`} sx={{ mb: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3} md={3}>
                  <DetailRow
                    item={{
                      label:
                        row?.labels?.examName?.replace?.(/^Language /, '') ??
                        '',
                      value: row?.examName ?? '',
                      isVerified: false,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3} md={3}>
                  <DetailRow
                    item={{
                      label:
                        row?.labels?.scoreReceived?.replace?.(
                          /^Language /,
                          ''
                        ) ?? '',
                      value:
                        typeof row?.scoreReceived === 'boolean'
                          ? row?.scoreReceived
                            ? 'Yes'
                            : 'No'
                          : (row?.scoreReceived ?? ''),
                      isVerified: false,
                    }}
                  />
                </Grid>
                {isScoreReceivedYes(row?.scoreReceived) && (
                    <>
                      <Grid item xs={12} sm={3} md={3}>
                        <DetailRow
                          item={{
                            label:
                              row?.labels?.examScore?.replace?.(
                                /^Language /,
                                ''
                              ) ?? '',
                            value: row?.examScore ?? '',
                            isVerified: false,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3} md={3}>
                        <DetailRow
                          item={{
                            label:
                              row?.labels?.totalMarks?.replace?.(
                                /^Language /,
                                ''
                              ) ?? '',
                            value: row?.totalMarks ?? '',
                            isVerified: false,
                          }}
                        />
                      </Grid>
                    </>
                  )}
                {isScoreReceivedNo(row?.scoreReceived) && (
                    <Grid item xs={12} sm={3} md={3}>
                      <DetailRow
                        item={{
                          label:
                            row?.labels?.scoreExpectedOn?.replace?.(
                              /^Core /,
                              ''
                            ) ?? '',
                          value: formatExpectedOnDate(row?.scoreExpectedOn),
                          isVerified: false,
                        }}
                      />
                    </Grid>
                  )}
              </Grid>
            </Grid>
          )
        )}
    </>
  );
};

export default EntranceExamDetailsTab;
